<template>
    <CForm @submit.prevent="handleSubmit">
        <CRow>
            <CCol md="6">
                <div class="h5">Основные настройки</div>
                
                <div class="mb-2">
                    <div class="col-form-label" for="name">Название рассылки</div>
                    <CInput
                        id="name"
                        v-model="formData.name"
                        placeholder="Введите название рассылки"
                        required
                    />
                </div>

                <div class="mb-2">
                    <div class="col-form-label" for="segment_id">Сегмент пользователей</div>
                    <CSelect
                        id="segment_id"
                        v-model="formData.segment_id"
                        class="mb-0"
                        :options="segmentOptions"
                        placeholder="Выберите сегмент"
                    />
                    <div class="text-muted">
                        Если не выбран сегмент, рассылка будет отправляться всем пользователям
                    </div>
                </div>

                <div class="mb-2">
                    <div class="col-form-label" for="trigger_type">Тип триггера</div>
                    <CSelect
                        id="trigger_type"
                        v-model="formData.trigger_type"
                        class="mb-0"
                        :options="triggerTypeOptions"
                        required
                    />
                </div>

                <div v-if="formData.trigger_type !== 'cart_abandoned' && formData.trigger_type !== 'checkout_abandoned'" class="mb-2">
                    <div class="col-form-label" for="trigger_days">Количество дней</div>
                    <CInput
                        id="trigger_days"
                        v-model.number="formData.trigger_days"
                        class="mb-0"
                        type="number"
                        min="1"
                        max="365"
                        :required="formData.trigger_type !== 'cart_abandoned' && formData.trigger_type !== 'checkout_abandoned'"
                    />
                    <div class="text-muted">
                        {{ getTriggerDaysHint() }}
                    </div>
                </div>

                <div v-else class="mb-2">
                    <div class="col-form-label" for="trigger_hours">Количество часов</div>
                    <CInput
                        id="trigger_hours"
                        v-model.number="formData.trigger_hours"
                        class="mb-0"
                        type="number"
                        min="1"
                        max="72"
                        :required="formData.trigger_type === 'cart_abandoned' || formData.trigger_type === 'checkout_abandoned'"
                    />
                    <div class="text-muted">
                        {{ getTriggerHoursHint() }}
                    </div>
                </div>

                <div class="mb-2">
                    <div class="col-form-label">Режим расписания</div>
                    <div class="mb-2">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input
                                id="schedule_mode_simple"
                                v-model="scheduleMode"
                                class="custom-control-input"
                                type="radio"
                                value="simple"
                            />
                            <label class="custom-control-label" for="schedule_mode_simple">
                                Простое время
                            </label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input
                                id="schedule_mode_cron"
                                v-model="scheduleMode"
                                class="custom-control-input"
                                type="radio"
                                value="cron"
                            />
                            <label class="custom-control-label" for="schedule_mode_cron">
                                Расписание (cron)
                            </label>
                        </div>
                    </div>
                </div>

                <div v-if="scheduleMode === 'simple'" class="mb-2">
                    <div class="col-form-label" for="send_time">Время рассылки</div>
                    <CInput
                        id="send_time"
                        v-model="formData.send_time"
                        class="mb-0"
                        type="time"
                        :required="scheduleMode === 'simple'"
                    />
                    <div class="text-muted">
                        Время в формате ЧЧ:ММ (24-часовой формат)
                    </div>
                </div>

                <div v-else class="mb-2">
                    <div class="col-form-label" for="send_schedule">Расписание отправки (cron)</div>
                    <CInput
                        id="send_schedule"
                        v-model="formData.send_schedule"
                        class="mb-0"
                        placeholder="0 9-18 * * *"
                        :required="scheduleMode === 'cron'"
                    />
                    <div class="text-muted mb-2">
                        Формат: минута час день месяц день_недели
                    </div>
                    
                    <!-- Примеры -->
                    <div class="card bg-light">
                        <div class="card-body p-2">
                            <div class="small">
                                <strong>Примеры:</strong>
                                <div 
                                    v-for="example in cronExamples" 
                                    :key="example.value"
                                    class="mb-1"
                                >
                                    <a 
                                        href="#" 
                                        @click.prevent="formData.send_schedule = example.value"
                                        class="text-primary"
                                    >
                                        {{ example.value }}
                                    </a>
                                    <span class="text-muted"> - {{ example.description }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="flex items-center">
                    <div class="flex items-center w-1/2">
                        <div class="col-form-label">Активна</div>
                        <CSwitch
                            class="ml-4"
                            color="success"
                            shape="pill"
                            variant="opposite"
                            :checked.sync="formData.enabled" />
                    </div>
                </div>
            </CCol>

            <CCol md="6">
                <div class="h5">Содержание рассылки</div>
                
                <div class="mb-2">
                    <div class="col-form-label" for="push_title">Заголовок push-уведомления</div>
                    <CInput
                        id="push_title"
                        v-model="formData.push_title"
                        class="mb-0"
                        placeholder="Введите заголовок"
                        maxlength="100"
                        required
                    />
                    <div class="text-muted">
                        Максимум 100 символов ({{ formData.push_title.length }}/100)
                    </div>
                </div>

                <div class="mb-2">
                    <div class="col-form-label" for="push_text">Текст push-уведомления</div>
                    <CTextarea
                        id="push_text"
                        v-model="formData.push_text"
                        class="mb-0"
                        placeholder="Введите текст уведомления"
                        rows="4"
                        maxlength="300"
                        required
                    />
                    <div class="text-muted">
                        Максимум 300 символов ({{ formData.push_text.length }}/300)
                    </div>
                </div>

                <div class="mb-2">
                    <div class="col-form-label" for="click_url">Ссылка при клике на push</div>
                    <CInput
                        id="click_url"
                        v-model="formData.click_url"
                        class="mb-0"
                        placeholder="https://example.com или /page"
                    />
                    <div class="text-muted">
                        Оставьте пустым, если не нужно перенаправление при клике
                    </div>
                </div>

                <!-- Предварительный просмотр -->
                <!-- <div class="mt-4">
                    <div class="h5">Предварительный просмотр</div>
                    <div class="card">
                        <div class="card-body">
                            <h6 class="card-title">{{ campaign.push_title || 'Без заголовка' }}</h6>
                            <p class="card-text">{{ campaign.push_text || 'Без текста' }}</p>
                            <small class="text-muted">
                                <strong>Ссылка при клике:</strong> 
                                {{ campaign.click_url || 'Не указана' }}
                            </small>
                        </div>
                    </div>
                </div> -->
            </CCol>
        </CRow>

        <hr class="my-4">

        <div class="d-flex justify-content-end">
            <CButton
                type="button"
                color="secondary"
                class="mr-2"
                @click="$emit('cancel')"
                :disabled="loading">
                Отмена
            </CButton>
            <CButton
                type="submit"
                color="primary"
                :disabled="loading || !isFormValid">
                <CSpinner v-if="loading" size="sm" class="mr-2"/>
                {{ campaign.id ? 'Обновить' : 'Создать' }}
            </CButton>
        </div>
    </CForm>
</template>

<script>
    export default {
        name: 'TriggerCampaignForm',

        props: {
            campaign: {
                type: Object,
                required: true
            },
            segments: {
                type: Array,
                default: () => []
            },
            loading: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                formData: { 
                    ...this.campaign,
                    trigger_hours: this.campaign.trigger_hours || null,
                    enabled: !!this.campaign.enabled,
                    deleted: !!this.campaign.deleted
                },
                scheduleMode: this.campaign.send_schedule ? 'cron' : 'simple',
                triggerTypeOptions: [
                    { value: 'registration', label: 'N-дней после регистрации' },
                    { value: 'last_order', label: 'N-дней после последнего заказа' },
                    { value: 'cart_abandoned', label: 'N-часов после добавления товара в корзину' },
                    { value: 'checkout_abandoned', label: 'N-часов после просмотра экрана оплаты' }
                ],
                cronExamples: [
                    { value: '0 9-18 * * *', description: 'Каждый час с 9:00 до 18:00' },
                    { value: '0 10-20 * * *', description: 'Каждый час с 10:00 до 20:00' },
                    { value: '0 */2 * * *', description: 'Каждые 2 часа' },
                    { value: '0 9,12,15,18 * * *', description: 'В 9:00, 12:00, 15:00 и 18:00' },
                    { value: '30 10-16 * * *', description: 'Каждый час в 30 минут с 10:30 до 16:30' },
                    { value: '0 9-18 * * 1-5', description: 'Каждый час с 9:00 до 18:00 (пн-пт)' }
                ]
            };
        },

        computed: {
            segmentOptions() {
                const options = [
                    { value: null, label: 'Все пользователи' }
                ];
                
                this.segments.forEach(segment => {
                    options.push({
                        value: segment.id,
                        label: segment.name
                    });
                });
                
                return options;
            },

            isFormValid() {
                // Проверяем наличие расписания
                const hasSchedule = this.scheduleMode === 'simple' 
                    ? !!this.formData.send_time 
                    : !!this.formData.send_schedule;

                const baseValid = !!(
                    this.formData.name &&
                    this.formData.trigger_type &&
                    hasSchedule &&
                    this.formData.push_title &&
                    this.formData.push_text
                );

                // Проверяем наличие trigger_days или trigger_hours в зависимости от типа триггера
                if (this.formData.trigger_type === 'cart_abandoned' || this.formData.trigger_type === 'checkout_abandoned') {
                    return baseValid && this.formData.trigger_hours > 0;
                } else {
                    return baseValid && this.formData.trigger_days > 0;
                }
            }
        },

        watch: {
            campaign: {
                handler(newCampaign) {
                    this.formData = { 
                        ...newCampaign,
                        trigger_hours: newCampaign.trigger_hours || null,
                        enabled: !!newCampaign.enabled,
                        deleted: !!newCampaign.deleted
                    };
                    this.scheduleMode = newCampaign.send_schedule ? 'cron' : 'simple';
                },
                deep: true,
                immediate: true
            },
            scheduleMode(newMode) {
                // При переключении режима очищаем неиспользуемое поле
                if (newMode === 'simple') {
                    this.formData.send_schedule = null;
                    if (!this.formData.send_time) {
                        this.formData.send_time = '10:00';
                    }
                } else {
                    this.formData.send_time = null;
                    if (!this.formData.send_schedule) {
                        this.formData.send_schedule = '0 9-18 * * *';
                    }
                }
            }
        },

        methods: {
            getTriggerDaysHint() {
                if (this.formData.trigger_type === 'registration') {
                    return `Рассылка будет отправлена через ${this.formData.trigger_days} дн. после регистрации пользователя`;
                } else if (this.formData.trigger_type === 'last_order') {
                    return `Рассылка будет отправлена через ${this.formData.trigger_days} дн. после последнего заказа пользователя`;
                }
                return '';
            },

            getTriggerHoursHint() {
                const hours = this.formData.trigger_hours || 1;
                if (this.formData.trigger_type === 'cart_abandoned') {
                    return `Рассылка будет отправлена через ${hours} ч. после добавления товара в корзину (если не было покупки)`;
                } else if (this.formData.trigger_type === 'checkout_abandoned') {
                    return `Рассылка будет отправлена через ${hours} ч. после просмотра экрана оплаты (если не было покупки)`;
                }
                return '';
            },

            handleSubmit() {
                if (!this.isFormValid) {
                    return;
                }

                // Очищаем пустые значения
                const cleanData = { 
                    ...this.formData,
                    enabled: !!this.formData.enabled,
                    deleted: !!this.formData.deleted
                };
                
                // Для cart_abandoned и checkout_abandoned используем trigger_hours, для остальных - trigger_days
                if (cleanData.trigger_type === 'cart_abandoned' || cleanData.trigger_type === 'checkout_abandoned') {
                    cleanData.trigger_days = null;
                } else {
                    cleanData.trigger_hours = null;
                }
                
                // Очищаем неиспользуемое поле расписания
                if (this.scheduleMode === 'simple') {
                    cleanData.send_schedule = null;
                } else {
                    cleanData.send_time = null;
                }
                
                if (!cleanData.click_url) {
                    cleanData.click_url = null;
                }
                if (!cleanData.segment_id) {
                    cleanData.segment_id = null;
                }

                this.$emit('submit', cleanData);
            }
        }
    };
</script>

<style scoped>
.card {
    border: 1px solid #dee2e6;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>
